/**
 * Pages/Panel/ReportesNomina/ReportePersonal.js
 *
 * "Personal": los trabajadores que estuvieron dentro de la planta en el rango, en
 * el layout que el cliente sube a su portal (una hoja `Data`, 10 columnas).
 *
 * La prueba de que alguien estuvo aquí es su RECIBO, no el catálogo: el padrón de
 * empleados dice quién está dado de alta hoy, no quién trabajó en julio. Por eso el
 * backend parte de las nóminas timbradas del rango.
 *
 * Un renglón por TRABAJADOR: quien tuvo cuatro recibos semanales sale una sola vez.
 * `NoFila` es el consecutivo del archivo, no un dato del sistema, así que se asigna
 * ya ordenado.
 *
 * Dos reglas del backend que explican los avisos naranjas de la pantalla: un recibo
 * SIN percepciones no prueba nada (la nómina histórica que se cargó con la fila en
 * blanco), y un finiquito sólo cuenta si de verdad pagó sueldo — la liquidación
 * pura, corrida en un periodo posterior a la baja, no dice que alguien estuvo aquí.
 *
 * El archivo destino es plano a propósito: lo lee un portal, no una persona. Sin
 * banda de título ni fila de totales, y el encabezado de la columna J trae el typo
 * "departamento/proyeto" del original — se reproduce tal cual.
 */

import { message, PageHeader, Select, Space, Spin, Tabs } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  TableItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
import { headerStyle } from "../../../Components/ExcelEstilos";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

// Los encabezados del archivo del cliente, literales. El typo de la última
// ("departamento/proyeto") está en el original y se reproduce tal cual: el portal
// cotea el encabezado, no la posición.
const HEADERS_PERSONAL = [
  "NoFila",
  "Nombre",
  "ApellidoPaterno",
  "ApellidoMaterno",
  "Curp",
  "Nss",
  "Rfc",
  "PeriodoPago",
  "SalarioBase",
  "departamento/proyeto",
];

// Anchos del archivo original, redondeados.
const ANCHOS_PERSONAL = [8, 24, 27, 32, 21, 24, 24, 22, 15, 21];

// `numFmt: "@"` es el formato TEXTO, y no es cosmético: en el archivo original las
// ocho columnas de texto están así, y es lo que evita que Excel reinterprete un NSS
// de 11 dígitos como número si alguien edita la celda después de descargar.
const CELDA_PERSONAL = { alignment: { vertical: "center" }, numFmt: "@" };
const CELDA_NUMERO = { alignment: { vertical: "center", horizontal: "right" }, numFmt: "0" };
// El original trae `#,##0.00`, NO el formato contable con "$" de moneyStyle: esta
// columna es un salario diario suelto, no un importe de un estado de cuenta.
const CELDA_SALARIO = {
  alignment: { vertical: "center", horizontal: "right" },
  numFmt: "#,##0.00",
};

// El NSS va como TEXTO en el archivo original y tiene que seguir yendo así: como
// número perdería los ceros a la izquierda y los 11 dígitos saldrían en notación
// científica. Lo mismo el CURP y el RFC, que ya son texto por naturaleza.
const celdaTexto = (valor) => ({
  v: valor === undefined || valor === null ? "" : String(valor),
  t: "s",
  s: CELDA_PERSONAL,
});

// xlsx-js-style no infiere el tipo cuando ya se le pasa un objeto {v,s} armado: sin
// "t" explícito escribe el número como texto y Excel no le aplica el formato.
const celdaNumero = (valor, estilo) =>
  (valor || valor === 0) && !Number.isNaN(Number(valor))
    ? { v: Number(valor), t: "n", s: estilo }
    : { v: "", t: "s", s: estilo };

class ReportePersonal extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    empresa: null,
    rango: null,
    // Un renglón por TRABAJADOR, ya ordenado y numerado por el backend.
    renglones: [],
    trabajadores: 0,
    // Los que tenían recibo en el rango pero se quedaron fuera. Son dos motivos
    // distintos y por eso van en dos contadores: el recibo vino sin percepciones, o
    // su único recibo era un finiquito que no pagó sueldo.
    enCeros: 0,
    soloFiniquito: 0,
    objetoRegistrosPatronales: null,
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: res.data.registrosPatronales.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  consultarReporte(values) {
    this.setState({ loading: true });
    const [fechaInicio, fechaFin] = values.periodo || [];
    axios
      .post(
        "reportesNomina/reportePersonal",
        {
          empresaId: this.props.empresaId,
          // El rango se compara contra los días del periodo de la prenómina, que
          // son fechas-día: se manda como "YYYY-MM-DD" para que parsearFechaDia lo
          // ancle igual sin depender de la zona horaria del navegador.
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          registrosPatronales: values.registrosPatronales || [],
          departamentos: values.departamentos || [],
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            consultado: true,
            empresa: res.data.empresa,
            rango: res.data.rango,
            renglones: res.data.renglones || [],
            trabajadores: res.data.trabajadores || 0,
            enCeros: res.data.enCeros || 0,
            soloFiniquito: res.data.soloFiniquito || 0,
          });
          if ((res.data.renglones || []).length === 0) {
            // No es lo mismo que "no hay timbres": puede haberlos y venir todos de
            // nóminas extraordinarias, que no prueban que nadie estuvo en la planta.
            message.info(
              "No hay ningún periodo ordinario timbrado dentro del rango"
            );
          }
        } else if (res.data.success === 1) {
          // Tope de filas o filtro incompleto: el backend dice cómo acotar.
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargo bien el reporte");
        this.setState({ loading: false });
      });
  }

  descargarExcel() {
    const { renglones, rango } = this.state;
    if (renglones.length === 0) return;

    const filas = [HEADERS_PERSONAL.map((h) => ({ v: h, t: "s", s: headerStyle }))];
    renglones.forEach((renglon) => {
      filas.push([
        celdaNumero(renglon.noFila, CELDA_NUMERO),
        celdaTexto(renglon.nombre),
        celdaTexto(renglon.apellidoPaterno),
        celdaTexto(renglon.apellidoMaterno),
        celdaTexto(renglon.curp),
        celdaTexto(renglon.nss),
        celdaTexto(renglon.rfc),
        celdaTexto(renglon.periodoPago),
        celdaNumero(renglon.salarioBase, CELDA_SALARIO),
        celdaTexto(renglon.departamento),
      ]);
    });

    const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
    hoja["!cols"] = ANCHOS_PERSONAL.map((wch) => ({ wch }));
    // El autofiltro sí se puede usar aquí: la advertencia de estilosExcelNomina es
    // que se traga las filas de Totales, y este archivo no tiene ninguna.
    hoja["!autofilter"] = { ref: `A1:J${renglones.length + 1}` };

    const libro = XLSXStyle.utils.book_new();
    // La hoja del archivo original se llama "Data"; se conserva el nombre.
    XLSXStyle.utils.book_append_sheet(libro, hoja, "Data");
    const mes = moment(rango?.fechaInicio, "DD/MM/YYYY");
    XLSXStyle.writeFile(
      libro,
      `Personal_${(mes.isValid() ? mes : moment()).format("MMM_YYYY").toUpperCase()}.xlsx`
    );
  }

  render() {
    const { loading, consultado, renglones } = this.state;

    // TableItem fuerza tableLayout fixed e ignora `scroll`, así que en pantalla van
    // sólo las columnas indispensables; el archivo lleva las diez.
    const columns = [
      { title: "#", dataIndex: "noFila", key: "noFila", align: "right", width: 60 },
      { title: "Nombre", dataIndex: "nombreCompleto", key: "nombreCompleto" },
      { title: "CURP", dataIndex: "curp", key: "curp", render: (v) => v || "—" },
      { title: "NSS", dataIndex: "nss", key: "nss", render: (v) => v || "—" },
      { title: "RFC", dataIndex: "rfc", key: "rfc", render: (v) => v || "—" },
      {
        title: "Periodo de pago",
        dataIndex: "periodoPago",
        key: "periodoPago",
        render: (v) => v || "—",
      },
      {
        title: "Salario base",
        dataIndex: "salarioBase",
        key: "salarioBase",
        align: "right",
        render: (valor) => dineroDisabledMask(valor || 0),
      },
      {
        title: "Departamento",
        dataIndex: "departamento",
        key: "departamento",
        render: (v) => v || "—",
      },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reportePersonal"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Personal"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {renglones.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
              </Tabs>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 10, color: "#666" }}>
                  {this.state.trabajadores} trabajador(es) con recibo
                  {this.state.rango
                    ? ` del ${this.state.rango.fechaInicio} al ${this.state.rango.fechaFin}`
                    : ""}
                  .
                  {this.state.enCeros > 0 && (
                    // Un dato, no un renglón: no reintroduce a nadie al archivo,
                    // pero evita que un recibo sin importe borre gente en silencio.
                    <span style={{ color: "#FA8C16" }}>
                      {` Se omitieron ${this.state.enCeros} trabajador(es) porque su recibo del rango vino en ceros (sin percepciones).`}
                    </span>
                  )}
                  {this.state.soloFiniquito > 0 && (
                    // Aviso propio: aquí no falta nada por capturar, es la regla del
                    // reporte — una liquidación sin sueldo no prueba presencia.
                    <span style={{ color: "#FA8C16" }}>
                      {` Otros ${this.state.soloFiniquito} quedaron fuera porque su único recibo del rango es un finiquito que no pagó sueldo.`}
                    </span>
                  )}
                </p>
                <TableItem
                  rowKey={(registro) => registro.clave}
                  columns={columns}
                  dataSource={renglones}
                  size="small"
                  defaultPageSize={25}
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReportePersonal);
